/**
 * API client with JWT bearer auth and silent refresh on 401.
 *
 * - Reads token/refresh from localStorage.
 * - On 401, attempts a single POST /api/refresh/ and retries the original.
 * - If refresh fails, calls /api/logout equivalent (clears tokens) and
 *   dispatches a 'auth:logout' window event for the router/auth store.
 */

const ACCESS_KEY = 'mt_token'
const REFRESH_KEY = 'mt_refresh'

export function getAccessToken(): string {
  return localStorage.getItem(ACCESS_KEY) || ''
}

export function getRefreshToken(): string {
  return localStorage.getItem(REFRESH_KEY) || ''
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access)
  localStorage.setItem(REFRESH_KEY, refresh)
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

interface ApiOptions {
  body?: unknown
  rawBody?: Blob
  signal?: AbortSignal
  formData?: FormData
  params?: Record<string, string | number | boolean | undefined>
  /** Skip the bearer token (e.g. for /api/login/). */
  anonymous?: boolean
  /** Override the default Content-Type. */
  contentType?: string
}

export class ApiError extends Error {
  status: number
  payload: unknown
  constructor(message: string, status: number, payload: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise
  const refresh = getRefreshToken()
  if (!refresh) return null
  refreshPromise = (async () => {
    try {
      const resp = await fetch('/api/refresh/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      })
      if (!resp.ok) {
        clearTokens()
        return null
      }
      const data = (await resp.json()) as { access: string }
      localStorage.setItem(ACCESS_KEY, data.access)
      return data.access
    } catch {
      clearTokens()
      return null
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

function buildUrl(
  template: string,
  params?: Record<string, string | number | boolean | undefined>,
): string {
  if (!params) return template
  let url = template
  const queryParams: string[] = []
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    const placeholder = `{${key}}`
    if (url.includes(placeholder)) {
      url = url.replace(placeholder, encodeURIComponent(String(value)))
    } else {
      queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    }
  }
  if (queryParams.length > 0) {
    url += (url.includes('?') ? '&' : '?') + queryParams.join('&')
  }
  return url
}

export async function apiFetch<T = unknown>(
  method: string,
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const url = buildUrl(path, options.params)
  const headers: Record<string, string> = {}
  if (!options.anonymous) {
    const token = getAccessToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }
  let body: BodyInit | undefined
  if (options.rawBody) {
    body = options.rawBody
    headers['Content-Type'] = 'application/octet-stream'
  } else if (options.formData) {
    body = options.formData
  } else if (options.body !== undefined) {
    body = JSON.stringify(options.body)
    headers['Content-Type'] = options.contentType ?? 'application/json'
  }

  const doRequest = async (): Promise<Response> => {
    return fetch(url, { method, headers, body, signal: options.signal })
  }

  let resp = await doRequest()
  if (resp.status === 401 && !options.anonymous) {
    const newAccess = await refreshAccessToken()
    if (newAccess) {
      headers['Authorization'] = `Bearer ${newAccess}`
      resp = await doRequest()
    } else {
      window.dispatchEvent(new CustomEvent('auth:logout'))
    }
  }
  if (!resp.ok) {
    let payload: unknown = null
    try {
      payload = await resp.json()
    } catch {
      try {
        payload = await resp.text()
      } catch {
        // ignore
      }
    }
    const detail =
      (typeof payload === 'object' && payload !== null && 'detail' in payload
        ? String((payload as Record<string, unknown>).detail)
        : null) ?? (Array.isArray(payload) ? payload.join(' ') :
        typeof payload === 'object' && payload !== null ? Object.entries(payload).slice(0, 3)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(' ') : String(value)}`).join('; ') : null) ?? resp.statusText
    throw new ApiError(detail || `HTTP ${resp.status}`, resp.status, payload)
  }
  const ct = resp.headers.get('content-type') || ''
  if (ct.includes('application/json')) {
    return (await resp.json()) as T
  }
  return (await resp.text()) as unknown as T
}

export async function apiDownload(path: string): Promise<{ blob: Blob; filename: string }> {
  const request = (token: string) => fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  let resp = await request(getAccessToken())
  if (resp.status === 401) {
    const newAccess = await refreshAccessToken()
    if (!newAccess) {
      window.dispatchEvent(new CustomEvent('auth:logout'))
      throw new ApiError('Authentication required', 401, null)
    }
    resp = await request(newAccess)
  }
  if (!resp.ok) {
    throw new ApiError(`Download failed: HTTP ${resp.status}`, resp.status, null)
  }
  const disposition = resp.headers.get('content-disposition') || ''
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]
  return {
    blob: await resp.blob(),
    filename: encoded ? decodeURIComponent(encoded) : (plain || 'export'),
  }
}
